import config from "../_config/config.const";
import { resolveBrandFromUrl } from "../_const/brandOrigin.const";
import { BrandEnum } from "../_common/enum/brand.enum";
import { mergeTrackingParams } from "./attribution.helper";

const PARENT_PATHNAME_KEY = "parentContext:pathname:v1";
const PARENT_QUERY_KEY = "parentContext:query:v1";

type QueuedParentMessage = {
    origin: string;
    data: { type?: string; pathname?: string; queryString?: string };
};

declare global {
    interface Window {
        __parentContextQueue?: QueuedParentMessage[];
    }
}

function normalizeOrigin(o: string) {
    return o ? o.replace(/\/+$/, "") : o;
}

export function getConfiguredParentOrigins(): string[] {
    const raw: unknown = (config as any).ORIGINS ?? (config as any).ORIGIN;
    if (Array.isArray(raw)) return raw.map(String).map(normalizeOrigin).filter(Boolean);
    if (typeof raw === "string" && raw.trim()) return [normalizeOrigin(raw)];
    return [];
}

export function persistParentPathname(pathname: string) {
    if (pathname) {
        sessionStorage.setItem(PARENT_PATHNAME_KEY, pathname);
    }
}

export function persistParentQueryString(queryString: string) {
    if (queryString) {
        sessionStorage.setItem(PARENT_QUERY_KEY, queryString);
    }
}

export function parentQueryHasExpiredLink() {
    const stored = sessionStorage.getItem(PARENT_QUERY_KEY);
    if (!stored) return false;
    return new URLSearchParams(stored).get("expired") === "1";
}

export function applyPersistedParentContext(
    pathnameRef: { current: string },
    queryParamsRef: { current: URLSearchParams }
): boolean {
    let applied = false;

    const storedPath = sessionStorage.getItem(PARENT_PATHNAME_KEY);
    if (storedPath && !pathnameRef.current) {
        pathnameRef.current = storedPath;
        applied = true;
    }

    const storedQuery = sessionStorage.getItem(PARENT_QUERY_KEY);
    if (storedQuery) {
        const incoming = new URLSearchParams(storedQuery);
        queryParamsRef.current = mergeTrackingParams(queryParamsRef.current, incoming);
        applied = true;
    }

    return applied;
}

export function drainEarlyParentContextQueue(
    pathnameRef: { current: string },
    queryParamsRef: { current: URLSearchParams },
    onOrigin?: (origin: string) => void
): boolean {
    const queue = window.__parentContextQueue;
    if (!queue?.length) return false;

    let applied = false;

    while (queue.length > 0) {
        const item = queue.shift();
        if (!item) continue;

        if (onOrigin && item.origin) {
            onOrigin(item.origin);
        }

        const d = item.data || {};

        if (d.type === "PARENT_PATHNAME" && typeof d.pathname === "string") {
            pathnameRef.current = d.pathname;
            persistParentPathname(d.pathname);
            applied = true;
        }

        if (d.type === "QUERY_STRING") {
            const incoming = new URLSearchParams(d.queryString || "");
            queryParamsRef.current = mergeTrackingParams(queryParamsRef.current, incoming);
            persistParentQueryString(d.queryString || "");
            applied = true;
        }
    }

    return applied;
}

export function requestParentContextFromIframe() {
    const origins = getConfiguredParentOrigins();

    if (origins.length === 0) {
        window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, "*");
        window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, "*");
        window.parent.postMessage({ type: "READY" }, "*");
        return;
    }

    for (const o of origins) {
        try {
            window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, o);
            window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, o);
            window.parent.postMessage({ type: "READY" }, o);
        } catch (err) {
            console.warn(`[parentContext] postMessage failed for "${o}":`, err);
        }
    }
}

export function resolveBrandFromParentOrigin(origin: string): BrandEnum | undefined {
    return resolveBrandFromUrl(origin);
}

/**
 * Safari / iOS often provides parent URL via document.referrer before postMessage.
 */
export function bootstrapParamsFromReferrer(): {
    pathname: string;
    searchParams: URLSearchParams;
} | null {
    if (!document.referrer) return null;

    try {
        const url = new URL(document.referrer);
        return {
            pathname: url.pathname,
            searchParams: url.searchParams,
        };
    } catch {
        return null;
    }
}

export function getParentOrigin(): string | null {
    try {
        const isFramed = window.self !== window.top;
        if (isFramed && document.referrer) {
            return new URL(document.referrer).href;
        }
    } catch {
        // ignore
    }

    try {
        return window.location.href || window.location.origin || null;
    } catch {
        return null;
    }
}

export function handleParentContextMessage(
    event: MessageEvent,
    pathnameRef: { current: string },
    queryParamsRef: { current: URLSearchParams },
    allowedOrigins: string[]
): { handled: boolean; brand?: BrandEnum } {
    const incomingOrigin = normalizeOrigin(event.origin);

    if (allowedOrigins.length > 0 && !allowedOrigins.includes(incomingOrigin)) {
        return { handled: false };
    }

    const brand = resolveBrandFromParentOrigin(event.origin);
    const data = (event.data || {}) as {
        type?: string;
        pathname?: string;
        queryString?: string;
    };

    if (data.type === "PARENT_PATHNAME" && typeof data.pathname === "string") {
        pathnameRef.current = data.pathname;
        persistParentPathname(data.pathname);
        return { handled: true, brand };
    }

    if (data.type === "QUERY_STRING") {
        const incoming = new URLSearchParams(data.queryString || "");
        queryParamsRef.current = mergeTrackingParams(queryParamsRef.current, incoming);
        persistParentQueryString(data.queryString || "");
        return { handled: true, brand };
    }

    return { handled: false, brand };
}
